import React, { useState, useEffect } from 'react';
import {
  Drawer,
  SelectPicker,
  Button,
  Table,
  IconButton,
  useToaster,
  Message,
  Whisper,
  Tooltip
} from 'rsuite';
import { useTranslation } from 'react-i18next';
import { fetchSetting, updateSetting } from '@/services/setting.service';
import TrashIcon from '@rsuite/icons/Trash';
import CheckIcon from '@rsuite/icons/Check';
import InfoRoundIcon from '@rsuite/icons/InfoRound';

const { Column, HeaderCell, Cell } = Table;

const NotificationDrawer = ({ open, onClose, users, usersLoading, toaster }) => {
  const { t } = useTranslation();
  const [notificationUsers, setNotificationUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [settingId, setSettingId] = useState(null);

  useEffect(() => {
    if (open) {
      fetchNotificationUsers();
    }
  }, [open]);

  const fetchNotificationUsers = async () => {
    setLoading(true);
    try {
      const setting = await fetchSetting('WFH_Notification');
      console.log('Fetched WFH_Notification setting:', setting);
      if (setting && setting.users) {
        setNotificationUsers(setting.users);
        setSettingId(setting._id);
      } else {
        toaster.push(
          <Message showIcon type="error" header={t('Error')}>
            {t('No settings found for WFH_Notification')}
          </Message>,
          { duration: 3000 }
        );
      }
    } catch (error) {
      console.error('Error fetching WFH_Notification:', error);
      toaster.push(
        <Message showIcon type="error" header={t('Error')}>
          {t('Failed to fetch notification users')}
        </Message>,
        { duration: 3000 }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async () => {
    console.log('handleAddUser called with selectedUser:', selectedUser);
    if (!selectedUser) {
      console.log('No user selected');
      toaster.push(
        <Message showIcon type="error" header={t('Error')}>
          {t('Please select a user')}
        </Message>,
        { duration: 3000 }
      );
      return;
    }

    const user = users.find(u => u._id === selectedUser);
    console.log('Selected user:', user);
    if (!user || !user.Email) {
      console.log('Invalid user or missing Email');
      toaster.push(
        <Message showIcon type="error" header={t('Error')}>
          {t('Invalid user selected')}
        </Message>,
        { duration: 3000 }
      );
      return;
    }

    const Email = user.Email;
    console.log('Checking if Email exists:', Email, notificationUsers);
    if (notificationUsers.includes(Email)) {
      console.log('Email already in notificationUsers');
      toaster.push(
        <Message showIcon type="error" header={t('Error')}>
          {t('User already in the list')}
        </Message>,
        { duration: 3000 }
      );
      return;
    }

    if (!settingId) {
      console.log('No settingId found');
      toaster.push(
        <Message showIcon type="error" header={t('Error')}>
          {t('Settings document not found')}
        </Message>,
        { duration: 3000 }
      );
      return;
    }

    setLoading(true);
    try {
      const updatedUsers = [...notificationUsers, Email];
      console.log('Updating WFH_Notification with users:', updatedUsers);
      await updateSetting(settingId, { users: updatedUsers });
      setNotificationUsers(updatedUsers);
      setSelectedUser(null);
      toaster.push(
        <Message showIcon type="success" header={t('Success')}>
          {t('User added successfully')}
        </Message>,
        { duration: 3000 }
      );
    } catch (error) {
      console.error('Error adding user:', error);
      toaster.push(
        <Message showIcon type="error" header={t('Error')}>
          {t('Failed to add user')}
        </Message>,
        { duration: 3000 }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async Email => {
    console.log('handleDeleteUser called with Email:', Email);
    if (!settingId) {
      console.log('No settingId found');
      toaster.push(
        <Message showIcon type="error" header={t('Error')}>
          {t('Settings document not found')}
        </Message>,
        { duration: 3000 }
      );
      return;
    }

    setLoading(true);
    try {
      const updatedUsers = notificationUsers.filter(u => u !== Email);
      console.log('Updating WFH_Notification with users:', updatedUsers);
      await updateSetting(settingId, { users: updatedUsers });
      setNotificationUsers(updatedUsers);
      toaster.push(
        <Message showIcon type="success" header={t('Success')}>
          {t('User removed successfully')}
        </Message>,
        { duration: 3000 }
      );
    } catch (error) {
      console.error('Error deleting user:', error);
      toaster.push(
        <Message showIcon type="error" header={t('Error')}>
          {t('Failed to remove user')}
        </Message>,
        { duration: 3000 }
      );
    } finally {
      setLoading(false);
    }
  };

  const userOptions = users
    .filter(user => !user.isBlocked)
    .map(user => ({
      label: `${user.firstname} ${user.lastname} (${user.Email})`,
      value: user._id,
      disabled: notificationUsers.includes(user.Email)
    }));

  const tableData = notificationUsers.map(Email => {
    const user = users.find(u => u.Email === Email);
    return {
      Email,
      firstname: user ? user.firstname : 'Unknown',
      lastname: user ? user.lastname : 'Unknown'
    };
  });

  return (
    <Drawer open={open} onClose={onClose} size="md">
      <Drawer.Header>
        <Drawer.Title>{t('Notification Settings')}</Drawer.Title>
      </Drawer.Header>
      <Drawer.Body>
        <div style={{ marginBottom: 20 }}>
          <h5>
            {t('Notification Receivers')}{' '}
            <Whisper
              placement="top"
              trigger="hover"
              speaker={
                <Tooltip>
                  {t('Users who receive Email notifications for Exceptional Home Office requests')}
                </Tooltip>
              }
            >
              <InfoRoundIcon style={{ fontSize: 14, marginLeft: 5, verticalAlign: 'middle' }} />
            </Whisper>
          </h5>
        </div>
        <div style={{ marginBottom: 20, display: 'flex', gap: 10 }}>
          <SelectPicker
            data={userOptions}
            value={selectedUser}
            onChange={setSelectedUser}
            placeholder={t('Select a user')}
            style={{ width: 300 }}
            searchable
            loading={usersLoading}
          />
          <Button appearance="primary" onClick={handleAddUser} disabled={loading || usersLoading}>
            <CheckIcon style={{ marginRight: 8 }} /> {t('Add User')}
          </Button>
        </div>
        <Table data={tableData} loading={loading || usersLoading} height={400}>
          <Column flexGrow={1}>
            <HeaderCell>{t('First Name')}</HeaderCell>
            <Cell dataKey="firstname" />
          </Column>
          <Column flexGrow={1}>
            <HeaderCell>{t('Last Name')}</HeaderCell>
            <Cell dataKey="lastname" />
          </Column>
          <Column flexGrow={1}>
            <HeaderCell>{t('Email')}</HeaderCell>
            <Cell dataKey="Email" />
          </Column>
          <Column width={80}>
            <HeaderCell>{t('Actions')}</HeaderCell>
            <Cell>
              {rowData => (
                <IconButton
                  icon={<TrashIcon style={{ color: 'red' }} />}
                  appearance="subtle"
                  onClick={() => handleDeleteUser(rowData.Email)}
                  disabled={loading || usersLoading}
                />
              )}
            </Cell>
          </Column>
        </Table>
      </Drawer.Body>
    </Drawer>
  );
};

export default NotificationDrawer;
